import React, { useEffect, useState } from "react";
import { api, formatEUR } from "@/lib/api";
import { Star, X, Search, UserPlus, Gift, Cake, PawPrint } from "lucide-react";
import { toast } from "sonner";

/**
 * Componente embeddabile nel modal di pagamento POS per:
 * - selezionare un cliente fedeltà (ricerca per nome/telefono)
 * - mostrare punti correnti e sconto riscattabile
 * - permettere all'operatore di confermare il riscatto in chiusura scontrino
 *
 * Props:
 *  - selected: customer object (or null)
 *  - onSelect(customer | null): callback when customer changes
 *  - redeem: number (punti da scalare)
 *  - onRedeemChange(points): callback (può essere 0)
 *  - testid?: prefix per i data-testid
 */
export default function CustomerLoyaltyPicker({ selected, onSelect, redeem, onRedeemChange, testid = "loy", mode = "" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [preview, setPreview] = useState(null);

  // Reload preview when selected customer changes
  useEffect(() => {
    if (!selected) { setPreview(null); return; }
    api.get(`/customers/${selected.id}/redeem-preview`).then(r => {
      setPreview(r.data);
      // Auto-suggerimento: se tenant ha auto_redeem e cliente è eligible, propone subito i punti riscattabili
      if (r.data.auto_redeem && r.data.eligible && !redeem) {
        onRedeemChange(r.data.redeemable_points);
      }
    }).catch(() => {});
  }, [selected?.id]);

  const search = async (q) => {
    setQuery(q);
    if (q.length < 2) { setResults([]); return; }
    const r = await api.get("/customers", { params: { q, limit: 10, mode: mode || undefined } });
    setResults(Array.isArray(r.data) ? r.data : []);
  };

  const pick = (c) => {
    onSelect(c);
    onRedeemChange(0);
    setOpen(false);
    setQuery("");
    setResults([]);
  };

  const clearCustomer = () => {
    onSelect(null);
    onRedeemChange(0);
    setPreview(null);
  };

  const createQuick = async () => {
    const name = query.trim();
    if (!name) return;
    try {
      const r = await api.post("/customers", { name, mode: mode || undefined });
      toast.success(`Cliente "${name}" creato`);
      pick(r.data);
    } catch {
      toast.error("Errore creazione cliente");
    }
  };

  const applyMax = () => {
    if (preview?.redeemable_points) onRedeemChange(preview.redeemable_points);
  };

  if (!selected) {
    return (
      <div className="border border-dashed border-velta-border rounded-md p-3 bg-velta-bg">
        {!open ? (
          <button
            data-testid={`${testid}-open-picker`}
            onClick={() => setOpen(true)}
            className="w-full h-10 inline-flex items-center justify-center gap-2 text-sm text-velta-mute hover:text-velta-ink font-medium"
          >
            <Star className="w-4 h-4" /> Aggiungi cliente fedeltà (opzionale)
          </button>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Search className="w-4 h-4 text-velta-mute shrink-0" />
              <input
                data-testid={`${testid}-search-input`}
                autoFocus
                placeholder="Cerca per nome o telefono…"
                value={query}
                onChange={(e) => search(e.target.value)}
                className="flex-1 h-9 px-2 bg-white border border-velta-border rounded text-sm focus:border-velta-ink outline-none"
              />
              <button onClick={() => { setOpen(false); setQuery(""); setResults([]); }} className="text-velta-mute"><X className="w-4 h-4" /></button>
            </div>
            {results.length > 0 && (
              <div className="bg-white border border-velta-border rounded max-h-40 overflow-auto">
                {(Array.isArray(results) ? results : []).map(c => (
                  <button
                    key={c.id}
                    data-testid={`${testid}-result-${c.id}`}
                    onClick={() => pick(c)}
                    className="w-full text-left px-3 py-2 hover:bg-velta-bg flex items-center justify-between text-sm border-b border-velta-border last:border-0"
                  >
                    <div>
                      <div className="font-medium flex items-center gap-1.5">
                        {c.name}
                        {mode === "toelettatura" && c.pet_birthday_today && <Cake data-testid={`${testid}-bday-${c.id}`} className="w-3.5 h-3.5 text-amber-500" />}
                      </div>
                      <div className="text-xs text-velta-mute">
                        {c.pet_name && mode === "toelettatura" && <span className="inline-flex items-center gap-0.5 mr-1"><PawPrint className="w-3 h-3" />{c.pet_name}</span>}
                        {c.phone || c.email || ""}
                      </div>
                    </div>
                    <div className="font-mono text-xs flex items-center gap-1 text-[#2F9E77]">
                      <Star className="w-3 h-3 fill-current" /> {c.loyalty_points || 0}
                    </div>
                  </button>
                ))}
              </div>
            )}
            {query.length >= 2 && results.length === 0 && (
              <button
                data-testid={`${testid}-create-quick`}
                onClick={createQuick}
                className="w-full h-9 inline-flex items-center justify-center gap-2 text-sm bg-white border border-velta-border rounded hover:border-velta-ink"
              >
                <UserPlus className="w-3.5 h-3.5" /> Crea cliente &quot;{query}&quot;
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  // Selected customer view
  return (
    <div className="border border-[#2F9E77]/40 bg-[#2F9E77]/5 rounded-md p-3" data-testid={`${testid}-customer-card`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Star className="w-4 h-4 text-[#2F9E77] fill-current" />
            <div className="font-medium truncate" data-testid={`${testid}-customer-name`}>{selected.name}</div>
          </div>
          <div className="text-xs text-velta-mute">
            {selected.phone && <span>{selected.phone} · </span>}
            <span className="font-mono">Punti attuali: <strong data-testid={`${testid}-customer-points`}>{preview?.points ?? selected.loyalty_points ?? 0}</strong></span>
          </div>
        </div>
        <button data-testid={`${testid}-clear-customer`} onClick={clearCustomer} className="text-velta-mute hover:text-velta-error"><X className="w-4 h-4" /></button>
      </div>

      {mode === "toelettatura" && selected.pet_birthday_today && (
        <div data-testid={`${testid}-bday-banner`} className="mb-2 flex items-center gap-2 bg-amber-50 border border-amber-300 rounded px-2 py-1.5 text-xs text-amber-800 font-medium">
          <Cake className="w-3.5 h-3.5 shrink-0" />
          Oggi è il compleanno di {selected.pet_name || "questo cliente"}!
          {selected.birthday_discount_enabled && <span className="font-bold">Sconto {Number(selected.birthday_discount_pct || 0)}%</span>}
        </div>
      )}

      {preview?.eligible && (
        <div className="bg-white rounded p-2 border border-velta-border">
          <div className="text-[11px] text-velta-mute mb-2 flex items-center gap-1">
            <Gift className="w-3 h-3" /> Sconto fedeltà disponibile (max {preview.redeemable_points} pt = {formatEUR(preview.redeemable_discount)})
          </div>
          <div className="flex items-center gap-2">
            <input
              data-testid={`${testid}-redeem-input`}
              type="number"
              step={preview.threshold}
              min={0}
              max={preview.redeemable_points}
              value={redeem || 0}
              onChange={(e) => {
                let v = parseInt(e.target.value) || 0;
                v = Math.max(0, Math.min(preview.redeemable_points, v));
                // arrotonda alla soglia
                v = Math.floor(v / preview.threshold) * preview.threshold;
                onRedeemChange(v);
              }}
              className="h-9 w-24 bg-white border border-velta-border rounded px-2 font-mono text-sm focus:border-velta-ink outline-none"
              placeholder="0"
            />
            <span className="text-xs text-velta-mute">pt =</span>
            <span className="font-mono font-bold text-[#2F9E77]" data-testid={`${testid}-redeem-discount`}>
              -{formatEUR(((redeem || 0) / preview.threshold) * preview.value_per_threshold)}
            </span>
            <button
              data-testid={`${testid}-redeem-max`}
              onClick={applyMax}
              className="ml-auto text-[11px] font-medium text-[#1E2D5B] hover:underline"
            >
              Usa max
            </button>
          </div>
        </div>
      )}
      {preview && !preview.eligible && (
        <div className="text-[11px] text-velta-mute">Servono almeno {preview.threshold} punti per riscattare (mancano {Math.max(0, preview.threshold - preview.points)}).</div>
      )}
    </div>
  );
}
